// @ds/forms/FormSection — section header + body wrapper for SchemaForm.
//
// Every CRUD form repeats the same SectionHeader pattern (Box size="sm"
// divider="bottom" + Eyebrow). Promote it. Adds opt-in conditional
// visibility based on watched form values.
//
// Tier: forms — composes display primitives (Eyebrow) inside a layout
// primitive (Box). Owns no state; reads form values via a callback the
// orchestrator provides. (When used outside the SchemaForm orchestrator,
// `visibleWhen` is undefined and the section renders unconditionally.)

import type { ReactNode } from "react";
import { Box } from "@ds/layout/Box";
import { Eyebrow } from "@ds/display/Eyebrow";

// ─── Inputs ──────────────────────────────────────────────────────────────

export interface FormSectionInput {
  /** Resolved title — typically `t(titleKey)` from the parent. Omit for an
   *  unlabeled section (children render flush to the previous block). */
  title?: ReactNode;
  /** Body content — the section's fields. */
  children: ReactNode;
  /** Pre-resolved visibility. When false, the section is not rendered.
   *  The orchestrator passes the result of `visibleWhen(values)`. */
  visible?: boolean;
  /** Optional test id on the section root — defaults to undefined (no testid). */
  testId?: string;
}

// No outputs, no actions — FormSection is purely presentational.

export type FormSectionProps = FormSectionInput;

// ─── Component ───────────────────────────────────────────────────────────

export function FormSection({
  title,
  children,
  visible = true,
  testId,
}: FormSectionProps) {
  if (!visible) return null;

  return (
    <div data-testid={testId}>
      {title != null && (
        <Box size="sm" divider="bottom">
          <Eyebrow>{title}</Eyebrow>
        </Box>
      )}
      {children}
    </div>
  );
}
